import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../hooks/useApi';
import { C } from '../components/UI';
import { LanguageSelector } from '../components/LanguageSelector';

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep]         = useState('login');
  const [form, setForm]         = useState({ email: '', password: '' });
  const [totpCode, setTotpCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = await authApi.login(form);
      if (data.mfaRequired) { setMfaToken(data.mfaToken); setStep('mfa'); }
      else { localStorage.setItem('ebios_token', data.token); localStorage.setItem('ebios_user', JSON.stringify(data.user)); navigate('/'); }
    } catch (err) { setError(err.response?.data?.error || t('auth.errorLogin')); }
    finally { setLoading(false); }
  }

  async function handleMfaVerify(e) {
    e.preventDefault();
    if (totpCode.length !== 6) { setError(t('mfa.errorCode')); return; }
    setError(''); setLoading(true);
    try {
      const data = await authApi.mfaLogin({ mfaToken, totpCode });
      localStorage.setItem('ebios_token', data.token); localStorage.setItem('ebios_user', JSON.stringify(data.user)); navigate('/');
    } catch (err) { setError(err.response?.data?.error || t('mfa.errorCode')); setTotpCode(''); }
    finally { setLoading(false); }
  }

  return (
    <div style={S.page}>
      {/* Left panel — always dark, atmospheric */}
      <div className="login-panel" style={S.panel}>
        <div style={S.panelContent}>
          <div style={S.shieldWrap}>
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" width="36" height="36">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <h1 style={S.panelTitle}>{t('app.name')}</h1>
          <p style={S.panelSub}>{t('app.tagline')}</p>

          <div style={S.ruler}>
            <div style={S.rulerLine} />
            <div style={{ display:'flex', gap:5, padding:'8px 0' }}>
              {[1,0.55,0.3,0.15,0.08].map((o,i) => (
                <div key={i} style={{ width:6, height:6, borderRadius:'50%', background:`rgba(96,165,250,${o})`, flexShrink:0 }} />
              ))}
            </div>
            <div style={S.rulerLine} />
          </div>

          <p style={S.panelCaption}>
            Analyse des risques conforme à la méthode ANSSI — EBIOS Risk Manager
          </p>
        </div>

        {/* Subtle grid overlay */}
        <div aria-hidden style={S.gridOverlay} />
      </div>

      {/* Right panel — form */}
      <div style={S.formPanel}>
        <div style={S.langBtn}><LanguageSelector /></div>

        <div style={S.formWrap} key={step}>

          {step === 'login' ? (
            <>
              <div style={S.formHeader}>
                <h2 style={S.formTitle}>{t('auth.login')}</h2>
                <p style={S.formDesc}>Accédez à votre espace de travail sécurisé</p>
              </div>

              <form onSubmit={handleLogin} style={S.form}>
                <div style={S.fgroup}>
                  <label style={S.flabel}>{t('auth.email')}</label>
                  <input style={S.finput} type="email" required autoFocus
                    value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    placeholder="vous@organisation.fr" />
                </div>
                <div style={S.fgroup}>
                  <label style={S.flabel}>{t('auth.password')}</label>
                  <input style={S.finput} type="password" required
                    value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                    placeholder="••••••••" />
                </div>
                {error && <div style={S.errorBox}>{error}</div>}
                <button type="submit" style={{ ...S.submitBtn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
                  {loading ? t('auth.connecting') : t('auth.connect')}
                </button>
              </form>

              <p style={S.footer}>
                {t('auth.noAccount')}{' '}
                <Link to="/register" style={S.link}>{t('auth.register')}</Link>
              </p>
            </>
          ) : (
            <>
              <div style={S.mfaIconWrap}>
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
                  <rect x="5" y="11" width="14" height="10" rx="2"/>
                  <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
                </svg>
              </div>
              <div style={S.formHeader}>
                <h2 style={S.formTitle}>{t('mfa.subtitle')}</h2>
                <p style={S.formDesc}>{t('mfa.desc')}</p>
              </div>

              <form onSubmit={handleMfaVerify} style={{ ...S.form, marginTop: 24 }}>
                <div style={S.fgroup}>
                  <label style={S.flabel}>{t('mfa.code')}</label>
                  <input style={{ ...S.finput, ...S.codeInput }} type="text" inputMode="numeric"
                    pattern="[0-9]{6}" maxLength={6} placeholder="000 000" autoFocus
                    value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g,'').slice(0,6))} />
                </div>
                {error && <div style={S.errorBox}>{error}</div>}
                <button type="submit"
                  style={{ ...S.submitBtn, opacity: loading || totpCode.length !== 6 ? 0.55 : 1 }}
                  disabled={loading || totpCode.length !== 6}>
                  {loading ? t('mfa.verifying') : t('mfa.verify')}
                </button>
                <button type="button" style={S.backBtn}
                  onClick={() => { setStep('login'); setError(''); setTotpCode(''); }}>
                  {t('mfa.back')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    background: 'var(--bg)',
    fontFamily: 'var(--font-sans)',
  },

  // Left atmospheric panel
  panel: {
    width: '42%',
    flexShrink: 0,
    background: '#050507',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  panelContent: {
    position: 'relative',
    zIndex: 1,
    padding: '40px',
    maxWidth: 340,
  },
  shieldWrap: {
    width: 68,
    height: 68,
    borderRadius: 14,
    background: 'rgba(37,99,235,0.12)',
    border: '1px solid rgba(96,165,250,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 28,
    boxShadow: '0 0 32px rgba(37,99,235,0.1)',
  },
  panelTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 26,
    fontWeight: 800,
    color: '#f4f4f5',
    letterSpacing: -0.6,
    marginBottom: 8,
    lineHeight: 1.1,
  },
  panelSub: {
    fontSize: 11,
    color: '#52525b',
    textTransform: 'uppercase',
    letterSpacing: 2.5,
    fontWeight: 500,
  },
  ruler: { margin: '28px 0' },
  rulerLine: {
    height: 1,
    background: 'linear-gradient(to right, rgba(96,165,250,0.35), transparent)',
    width: '100%',
  },
  panelCaption: {
    fontSize: 12,
    color: '#3f3f46',
    lineHeight: 1.65,
  },
  gridOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(96,165,250,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(96,165,250,0.04) 1px, transparent 1px)
    `,
    backgroundSize: '32px 32px',
    pointerEvents: 'none',
  },

  // Right form panel
  formPanel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    position: 'relative',
    background: 'var(--bg)',
  },
  langBtn: { position: 'absolute', top: 20, right: 22 },
  formWrap: {
    width: '100%',
    maxWidth: 360,
    animation: 'fadeUp 0.25s ease both',
  },
  formHeader: { marginBottom: 28 },
  formTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: -0.4,
    marginBottom: 6,
  },
  formDesc: { fontSize: 13, color: 'var(--text3)' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  fgroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  flabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text3)',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  finput: {
    background: 'var(--bg-muted)',
    border: '1px solid var(--border)',
    color: 'var(--text)',
    padding: '10px 13px',
    borderRadius: 6,
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'border-color .12s',
  },
  codeInput: {
    fontSize: 22,
    letterSpacing: 10,
    textAlign: 'center',
    fontFamily: 'var(--font-mono)',
  },
  errorBox: {
    background: 'var(--red-bg)',
    border: '1px solid var(--red-border)',
    color: 'var(--red)',
    padding: '10px 13px',
    borderRadius: 6,
    fontSize: 12.5,
  },
  submitBtn: {
    background: 'var(--blue)',
    color: 'white',
    border: 'none',
    padding: '11px 20px',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    letterSpacing: -0.2,
    transition: 'opacity .12s',
    width: '100%',
  },
  backBtn: {
    background: 'transparent',
    color: 'var(--text3)',
    border: '1px solid var(--border)',
    padding: '10px 20px',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    width: '100%',
  },
  footer: { textAlign: 'center', fontSize: 13, color: 'var(--text3)', marginTop: 20 },
  link:   { color: 'var(--blue)', textDecoration: 'none', fontWeight: 500 },
  mfaIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 12,
    background: 'var(--blue-bg)',
    border: '1px solid var(--blue-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
};
